import { AddressSubSchema } from '@updater/ui-informant';
import type { SuperSchema } from '@updater/ui-informant';
import { add, sub } from 'date-fns';
import { EMAIL_REGX, PHONE_NUMBER_REGX } from 'constants/regx';
import { CountryData } from '@updater/ui-widgets';

// Type for layout
type LayoutField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'password'
  | 'moveDate'
  | 'homeSize'
  | 'ownership'
  | 'movingFromOutsideCountry'
  | 'phone'
  | 'optIn';

// Schema properties
const schemaProperties = (
  moveDate: Date
): Record<LayoutField, SuperSchema> => ({
  firstName: {
    type: 'string',
    minLength: 2,
    maxLength: 255,
    ui: {
      label: 'First name',
      inputType: 'text',
      width: [1, 1, 1, 0.5],
      messages: {
        required: 'First name is required!',
        minLength:
          'The first name should have a minimal length of 2 characters',
      },
    },
  },
  lastName: {
    type: 'string',
    minLength: 2,
    maxLength: 255,
    ui: {
      label: 'Last name',
      width: [1, 1, 1, 0.5],
      messages: {
        required: 'Last name is required!',
        minLength: 'The last name should have a minimal length of 2 characters',
      },
    },
  },
  email: {
    title: 'email',
    type: 'string',
    pattern: EMAIL_REGX,
    ui: {
      label: 'Email',
      messages: {
        pattern: 'Please enter a valid email address',
      },
      validate: 'verifyEmailAvailability',
      validateOnChange: false,
    },
  },

  password: {
    title: 'password',
    type: 'string',
    minLength: 6,
    ui: {
      label: 'Choose a password',
      validate: 'validatePassword',
      component: 'security',
      messages: {
        minLength: 'Password must be at least 6 characters',
        required: 'Please enter a password',
      },
    },
  },

  moveDate: {
    title: 'date',
    type: 'string',
    ui: {
      component: 'datePicker',
      label: 'Move date',
      heading: 'When are you moving?',
      componentProps: {
        maxDate: add(moveDate, { months: 6 }),
        minDate: sub(moveDate, { months: 1 }),
      },
    },
  },
  homeSize: {
    title: 'homesize',
    type: 'string',
    oneOf: [
      {
        const: 'studio',
        title: 'Studio',
      },
      {
        const: 'one_bed',
        title: 'One Bedroom',
      },
      {
        const: 'two_bed',
        title: 'Two Bedroom',
      },
      {
        const: 'three_bed',
        title: 'Three Bedroom',
      },
      {
        const: 'four_bed_plus',
        title: 'Four Bedroom +',
      },
    ],
    ui: {
      component: 'select',
      options: [
        { label: 'Studio', value: 'studio', id: 'studio' },
        {
          id: 'one-bedroom',
          label: 'One Bedroom',
          value: 'one-bedroom',
        },
        {
          id: 'two-bedroom',
          label: 'Two Bedroom',
          value: 'two-bedroom',
        },
        {
          id: 'three-bedroom',
          label: 'Three Bedroom',
          value: 'three-bedroom',
        },
        {
          id: 'four-bedroom-plus',
          label: 'Four Bedroom +',
          value: 'four-bedroom-plus',
        },
      ],
      label: 'Home size',
      heading: 'What’s the size of your current home?',
    },
  },
  ownership: {
    title: 'ownership',
    type: 'string',
    default: 'RENT',
    oneOf: [
      {
        const: 'RENT',
        title: 'Rent',
      },
      {
        const: 'OWN',
        title: 'Own',
      },
    ],
    ui: {
      heading: 'Will you rent or own your new home?',
      component: 'radiolist',
      width: 1,
      componentProps: {
        direction: ['vertical', 'vertical', 'horizontal'],
        controlSize: 'l',
        distributeEqually: true,
      },
    },
  },
  movingFromOutsideCountry: {
    type: 'string',
    default: 'No',
    oneOf: [
      {
        const: 'Yes',
        title: 'Yes',
      },
      {
        const: 'No',
        title: 'No',
      },
    ],
    ui: {
      hide: true,
    },
  },
  phone: {
    title: 'phone',
    type: 'string',
    pattern: PHONE_NUMBER_REGX,
    ui: {
      component: 'phone',
      label: 'Phone number',
      validate: 'validatePhone',
      messages: {
        required: 'Please enter a valid phone number',
        minLength: 'Please enter a valid phone number',
      },
    },
  },
  optIn: {
    type: 'boolean',
    default: true,
    ui: {
      component: 'checkbox',
      componentProps: {
        size: 'm',
      },
      label:
        'By checking this box, I authorize Updater to call or text me at the telephone number above regarding personal moving assistance, special offers, and marketing messages. I acknowledge I’m not required to receive these communications as a condition of using Updater’s products or services. I understand message and data rates may apply.',
    },
  },
});

/* Elements that handle international address */
const countryOneOf = Object.keys(CountryData)
  .filter((k) => k !== 'US')
  .map((k) => ({
    const: k,
    title: CountryData[k],
  }));

const fromOutsideCountryAddressSchema: SuperSchema = {
  type: 'object',
  properties: {
    street: {
      type: 'string',
      title: 'Street',
      minLength: 1,
      ui: {
        label: 'Street',
        messages: { minLength: 'Street is required' },
      },
    },
    unit: {
      type: 'string',
      pattern: '^[\\w\\s]{0,10}$',
      title: 'Unit',
      ui: {
        label: 'Unit',
      },
    },
    city: {
      type: 'string',
      title: 'City',
      minLength: 1,
      ui: {
        label: 'City',
        messages: { minLength: 'City is required' },
      },
    },
    country: {
      type: 'string',
      oneOf: countryOneOf,
      title: 'Country',
      minLength: 2,
      ui: {
        label: 'Country',
        messages: { minLength: 'Country is required' },
        componentProps: {
          autoComplete: 'country',
          maxLength: 2,
        },
      },
    },
  },
  required: ['street', 'city', 'country'],
  ui: { component: 'FromOutsideCountryAddress' },
};

// Returns the data schema
export const getSchemaUpo = (): SuperSchema => {
  const getProperty = (): Record<LayoutField, SuperSchema> => {
    const schema = schemaProperties(new Date());
    return {
      ...schema,
      firstName: {
        ...schema.firstName,
        ui: {
          ...schema.firstName.ui,
          width: 1,
        },
      },
      lastName: {
        ...schema.lastName,
        ui: {
          ...schema.lastName.ui,
          width: 1,
        },
      },
      homeSize: {
        ...schema.homeSize,
        ui: {
          ...schema.homeSize.ui,
          heading: undefined,
          label: 'Current home size',
        },
      },
      ownership: {
        ...schema.ownership,
        ui: {
          ...schema.ownership.ui,
          heading: undefined,
          componentProps: {
            ...schema?.ownership?.ui?.componentProps,
            direction: 'vertical',
          },
        },
      },
      phone: {
        ...schema.phone,
        ui: {
          ...schema.phone.ui,
          heading: undefined,
          componentProps: {
            ...schema?.phone?.ui?.componentProps,
          },
        },
      },
      optIn: {
        ui: {
          ...schema.optIn.ui,
          heading: undefined,
          componentProps: {
            ...schema?.optIn?.ui?.componentProps,
          },
          label:
            'By checking the box, you are authorizing Updater to deliver to you, at the above telephone number, personal moving assistance, special offers, and marketing messages. You are not required to agree to receive such messaging (either directly or indirectly) as a condition of purchasing any of Updater’s products or services. Message and data rates may apply.',
        },
      },
    };
  };

  // The required fields
  const getRequired = () => {
    return [
      'firstName',
      'lastName',
      'email',
      'password',
      'phone',
      'optIn',
      'moveDate',
      'homeSize',
      'movingFromOutsideCountry',
    ] as LayoutField[];
  };

  // Transformed constructed schema w/props and required
  return {
    type: 'object',
    properties: getProperty(),
    required: getRequired(),
    allOf: [
      {
        if: {
          properties: {
            movingFromOutsideCountry: {
              const: 'No',
            },
          },
        },
        then: {
          properties: {
            fromAddress: {
              ...AddressSubSchema,
              ...{ title: 'fromAddress' },
              ui: {
                component: 'FromUsaAddress',
                componentProps: {},
              },
            },
          },
          required: ['fromAddress'],
        },
      },
      {
        if: {
          properties: {
            movingFromOutsideCountry: {
              const: 'Yes',
            },
          },
        },
        then: {
          properties: {
            fromOutsideCountryAddress: { ...fromOutsideCountryAddressSchema },
          },
          required: ['fromOutsideCountryAddress'],
        },
      },
    ],
  };
};
